import { RELOAD_GUARD_KEY, RELOAD_PARAM } from "./constants";

// Turn a saved value into a real timestamp, or 0 if it's missing or junk.
// (Junk would become NaN, which sneaks past the "did we just reload?" check
// and would let us reload forever.)
const toTimestamp = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

// Read the saved reload time. 0 if storage is blocked, empty, or junk.
const readSessionStorage = (): number => {
  try {
    return toTimestamp(sessionStorage.getItem(RELOAD_GUARD_KEY));
  } catch {
    return 0;
  }
};

const writeSessionStorage = (now: number): boolean => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    return true;
  } catch {
    // in-app browsers (WebViews) can block session storage
    return false;
  }
};

const readUrlParam = (): number => {
  if (typeof window === "undefined") return 0;
  const params = new URLSearchParams(window.location.search);
  return toTimestamp(params.get(RELOAD_PARAM));
};

// Stash the timestamp in the URL so it survives the reload even when storage is
// blocked. replaceState updates the URL that location.reload() re-requests,
// without adding a history entry.
const writeUrlParam = (now: number): void => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(now));
  window.history.replaceState(window.history.state, "", url.toString());
};

// When did we last reload? 0 if never. Use the newest time either source knows
// about. If a sessionStorage write failed and we fell back to the URL, reading
// the (empty) sessionStorage alone would say "never" and let the reload loop
// start over — so we never let one empty source hide another that remembers.
export const getLastReloadAt = (): number => {
  return Math.max(readSessionStorage(), readUrlParam());
};

// Writes both stores every time. The URL param is not only a fallback: it gives
// the reload a new query string. Without it, iOS WebKit re-serves the stale
// document from cache or bfcache and the reload fetches nothing new.
export const setLastReloadAt = (now: number): void => {
  writeSessionStorage(now);
  writeUrlParam(now);
};
